// Site Detail View
import { getSite, updateSite, inviteUserToSite, removeUserFromSite, updateUserPermissions } from '../firebase/db.js';
import { getCurrentUser } from '../firebase/auth.js';
import router from '../router.js';
import { createModal, showModal, hideModal, initModals, confirm } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { t } from '../i18n.js';

/**
 * Create site detail view
 * @param {Object} site - Site data
 * @returns {string} HTML string
 */
export function createSiteDetailView(site) {
  if (!site) {
    return `
      <div class="page">
        <div class="container">
          <div class="empty-state">
            <div class="empty-state-icon">❌</div>
            <h2 class="empty-state-title">${t('app.error')}</h2>
            <p class="empty-state-text">İstenen kazı alanı bulunamadı veya erişim yetkiniz yok.</p>
            <a href="#/dashboard" class="btn btn-primary">${t('nav.dashboard')}</a>
          </div>
        </div>
      </div>
    `;
  }

  const currentUser = getCurrentUser();
  const isOwner = currentUser && site.userId === currentUser.uid;
  const userPermissions = site.permissions?.[currentUser.email] || {};

  const periods = {
    neolithic: t('dashboard.sitePeriod') + ' - Neolithic',
    bronze_age: t('dashboard.sitePeriod') + ' - Bronze Age',
    iron_age: t('dashboard.sitePeriod') + ' - Iron Age',
    classical: t('dashboard.sitePeriod') + ' - Classical',
    medieval: t('dashboard.sitePeriod') + ' - Medieval',
    other: t('dashboard.sitePeriod') + ' - Other'
  };

  return `
    <div class="page">
      <div class="container">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
          <a href="#/dashboard">${t('nav.dashboard')}</a>
          <span class="breadcrumb-sep">›</span>
          <span>${escapeHtml(site.name)}</span>
        </div>
        
        <!-- Site Header -->
        <div class="site-header card">
          <div class="site-header-content flex-mobile-column">
            <div class="site-header-icon">🏛️</div>
            <div class="site-header-info">
              <h1 class="site-header-title">${escapeHtml(site.name)}</h1>
              <p class="site-header-location">
                📍 ${escapeHtml(site.location) || '-'}
              </p>
              <div class="site-header-meta">
                ${site.period ? `<span class="badge badge-primary">${periods[site.period] || site.period}</span>` : ''}
                ${site.excavationCode ? `<span class="badge">Code: ${escapeHtml(site.excavationCode)}</span>` : ''}
                ${site.supervisor ? `<span class="text-sm text-muted">${t('site.director')}: ${escapeHtml(site.supervisor)}</span>` : ''}
                ${!isOwner ? `<span class="badge badge-warning" style="background: var(--color-accent-secondary);">Member</span>` : ''}
              </div>
            </div>
            <div class="site-header-actions mt-4 w-full flex gap-2">
              ${isOwner ? `
                <button class="btn btn-secondary flex-1" id="manage-team-btn">👥 ${t('site.members')}</button>
                <button class="btn btn-secondary flex-1" id="edit-site-btn">✏️ ${t('site.edit')}</button>
              ` : ''}
            </div>
          </div>
          ${site.description ? `
            <div class="site-description">
              <p>${escapeHtml(site.description)}</p>
            </div>
          ` : ''}
          ${site.coordinates ? `
            <div class="site-coords text-sm text-muted">
              🌐 ${escapeHtml(site.coordinates)}
            </div>
          ` : ''}
        </div>
        
        <!-- Quick Actions -->
        <div class="site-actions grid grid-cols-4 gap-4">
          <a href="#/site/${site.id}/logbook" class="action-card card">
            <div class="action-icon">📓</div>
            <h3 class="action-title">${t('site.tabLogbook')}</h3>
            <span class="action-count">${site.logCount || 0}</span>
          </a>
          
          <a href="#/site/${site.id}/assets" class="action-card card">
            <div class="action-icon">🗿</div>
            <h3 class="action-title">${t('site.tabAssets')}</h3>
            <span class="action-count">${site.assetCount || 0}</span>
          </a>
          
          <a href="#/site/${site.id}/reconstruct" class="action-card card">
            <div class="action-icon">🧩</div>
            <h3 class="action-title">${t('site.tabReconstruct')}</h3>
            <span class="action-count">Ready</span>
          </a>
          
          <div class="action-card card action-card-disabled">
            <div class="action-icon">📊</div>
            <h3 class="action-title">${t('site.analyses')}</h3>
            <p class="action-desc">${t('site.analysesSub')}</p>
            <span class="action-count">${t('site.comingSoon')}</span>
          </div>
        </div>
        
        <!-- Recent Activity -->
        <div class="site-activity mt-8">
          <h2 class="text-xl mb-4">${t('site.recentActivity')}</h2>
          <div class="activity-list card">
            <div class="empty-state" style="padding: var(--space-8);">
              <p class="text-muted">${t('site.noActivity')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Edit Site Modal -->
    ${createModal('edit-site-modal', t('site.edit'), createEditSiteForm(site), {
    confirmText: t('app.save'),
    size: 'large'
  })}
  
  <!-- Team Management Modal -->
  ${isOwner ? createModal('team-modal', t('site.members'), createTeamModalContent(site), {
    showFooter: false,
    size: 'large'
  }) : ''}
    
    <style>
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-6);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }
      
      .breadcrumb a {
        color: var(--color-text-secondary);
      }
      
      .breadcrumb a:hover {
        color: var(--color-accent-primary);
      }
      
      .site-header {
        margin-bottom: var(--space-6);
      }
      
      .site-header-content {
        display: flex;
        align-items: flex-start;
        gap: var(--space-5);
      }
      
      .site-header-icon {
        font-size: 4rem;
        opacity: 0.8;
      }
      
      .site-header-info {
        flex: 1;
      }
      
      .site-header-title {
        font-size: var(--text-3xl);
        margin-bottom: var(--space-2);
      }
      
      .site-header-location {
        color: var(--color-text-secondary);
        margin-bottom: var(--space-3);
      }
      
      .site-header-meta {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        flex-wrap: wrap;
      }
      
      .site-description {
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--color-border);
        color: var(--color-text-secondary);
      }
      
      .site-coords {
        margin-top: var(--space-3);
      }
      
      .action-card {
        text-align: center;
        cursor: pointer;
        text-decoration: none;
        color: inherit;
      }
      
      .action-card:hover {
        border-color: var(--color-accent-primary);
      }
      
      .action-card-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .action-card-disabled:hover {
        border-color: var(--color-border);
        transform: none;
      }
      
      .action-icon {
        font-size: 3rem;
        margin-bottom: var(--space-3);
      }
      
      .action-title {
        font-size: var(--text-lg);
        margin-bottom: var(--space-2);
      }
      
      .action-desc {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-3);
      }
      
      .action-count {
        font-size: var(--text-sm);
        color: var(--color-accent-primary);
        font-weight: 500;
      }
      
      /* Team Modal Styles */
      .team-list {
        margin-bottom: var(--space-6);
      }
      
      .team-member {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-3);
        background: var(--color-bg-tertiary);
        margin-bottom: var(--space-2);
        border-radius: var(--radius-md);
      }
      
      .member-info {
        display: flex;
        flex-direction: column;
      }
      
      .member-email {
        font-weight: 500;
      }
      
      .member-perms {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 4px;
        display: flex;
        gap: 6px;
      }
      
      .perm-badge {
        background: rgba(255, 255, 255, 0.1);
        padding: 2px 6px;
        border-radius: 4px;
      }
      
      .perm-section-title {
        font-size: var(--text-sm);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-3);
        border-bottom: 1px solid var(--color-border);
        padding-bottom: var(--space-2);
      }
      
      .invite-form {
        background: var(--color-bg-secondary);
        padding: var(--space-4);
        border-radius: var(--radius-lg);
      }
      
      .permissions-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-3);
        margin: var(--space-3) 0;
      }
      
      .checkbox-label {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        cursor: pointer;
        font-size: var(--text-sm);
      }
      
      .checkbox-label input {
        width: 16px;
        height: 16px;
        accent-color: var(--color-accent-primary);
      }
    </style>
  `;
}

/**
 * Create edit site form HTML
 * @param {Object} site 
 * @returns {string} HTML string
 */
function createEditSiteForm(site) {
  return `
    <form id="edit-site-form">
      <div class="form-group">
        <label class="form-label" for="edit-site-name">Alan Adı *</label>
        <input 
          type="text" 
          id="edit-site-name" 
          class="form-input" 
          value="${escapeHtml(site.name)}"
          required
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-location">Konum</label>
        <input 
          type="text" 
          id="edit-site-location" 
          class="form-input" 
          value="${escapeHtml(site.location || '')}"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-coords">Koordinatlar</label>
        <input 
          type="text" 
          id="edit-site-coords" 
          class="form-input" 
          value="${escapeHtml(site.coordinates || '')}"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-period">Tarihi Dönem</label>
        <select id="edit-site-period" class="form-select">
          <option value="">Bir dönem seçin</option>
          <option value="neolithic" ${site.period === 'neolithic' ? 'selected' : ''}>Neolitik</option>
          <option value="bronze_age" ${site.period === 'bronze_age' ? 'selected' : ''}>Tunç Çağı</option>
          <option value="iron_age" ${site.period === 'iron_age' ? 'selected' : ''}>Demir Çağı</option>
          <option value="classical" ${site.period === 'classical' ? 'selected' : ''}>Klasik Antik Çağ</option>
          <option value="medieval" ${site.period === 'medieval' ? 'selected' : ''}>Orta Çağ</option>
          <option value="other" ${site.period === 'other' ? 'selected' : ''}>Diğer</option>
        </select>
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-supervisor">Kazı Başkanı</label>
        <input 
          type="text" 
          id="edit-site-supervisor" 
          class="form-input" 
          value="${escapeHtml(site.supervisor || '')}"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-code">Kazı Kodu</label>
        <input 
          type="text" 
          id="edit-site-code" 
          class="form-input" 
          value="${escapeHtml(site.excavationCode || '')}"
        >
      </div>
      
      <div class="form-group">
        <label class="form-label" for="edit-site-description">Açıklama</label>
        <textarea 
          id="edit-site-description" 
          class="form-textarea"
          rows="3"
        >${escapeHtml(site.description || '')}</textarea>
      </div>
    </form>
  `;
}

/**
 * Create Team Modal Content
 * @param {Object} site 
 */
function createTeamModalContent(site) {
  const shares = site.shares || [];
  const permissions = site.permissions || {};

  return `
      <div>
        <div class="perm-section-title">Mevcut Ekip (${shares.length})</div>
        <div class="team-list" id="team-list">
            ${shares.length > 0 ? shares.map(email => {
    const perms = permissions[email] || {};
    const permLabels = [];
    if (perms.canCreateLog) permLabels.push('Kayıt+');
    if (perms.canDeleteLog) permLabels.push('Kayıt-');
    if (perms.canUploadAsset) permLabels.push('Dosya+');
    if (perms.canDeleteAsset) permLabels.push('Dosya-');

    return `
                <div class="team-member">
                    <div class="member-info">
                        <span class="member-email">${escapeHtml(email)}</span>
                        <div class="member-perms">
                            ${permLabels.length ? permLabels.map(l => `<span class="perm-badge">${l}</span>`).join('') : '<span class="perm-badge">Salt Okunur</span>'}
                        </div>
                    </div>
                    <button class="btn btn-ghost btn-sm text-red-400 remove-member-btn" data-email="${email}">Kaldır</button>
                </div>
                `;
  }).join('') : '<p class="text-muted text-sm italic">Henüz üye eklenmemiş.</p>'}
        </div>
        
        <div class="perm-section-title">${t('site.addMember')}</div>
        <div class="invite-form">
            <div class="form-group">
                <label class="form-label">${t('site.enterEmail')}</label>
                <input type="email" id="invite-email" class="form-input" placeholder="ornek@email.com">
            </div>
            
            <label class="form-label mb-2">İzinler</label>
            <div class="permissions-grid">
                <label class="checkbox-label">
                    <input type="checkbox" id="perm-create-log" checked>
                    Kayıt Oluşturabilir
                </label>
                <label class="checkbox-label">
                    <input type="checkbox" id="perm-delete-log">
                    Kayıt Silebilir
                </label>
                <label class="checkbox-label">
                    <input type="checkbox" id="perm-upload-asset" checked>
                    Dosya Yükleyebilir
                </label>
                <label class="checkbox-label">
                    <input type="checkbox" id="perm-delete-asset">
                    Dosya Silebilir
                </label>
            </div>
            
            <button class="btn btn-primary w-full mt-4" id="send-invite-btn">Davet Et</button>
        </div>
      </div>
    `;
}

/**
 * Initialize site detail view event listeners
 * @param {Object} site 
 */
export function initSiteDetailView(site) {
  if (!site) return;

  initModals();

  // Edit button
  const editBtn = document.getElementById('edit-site-btn');
  if (editBtn) {
    editBtn.addEventListener('click', () => showModal('edit-site-modal'));
  }

  // Save changes
  const confirmBtn = document.querySelector('#edit-site-modal [data-modal-confirm]');
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => handleUpdateSite(site.id));
  }

  // Team Management
  const teamBtn = document.getElementById('manage-team-btn');
  if (teamBtn) {
    teamBtn.addEventListener('click', () => showModal('team-modal'));
  }

  const inviteBtn = document.getElementById('send-invite-btn');
  if (inviteBtn) {
    inviteBtn.addEventListener('click', () => handleInviteUser(site));
  }

  document.querySelectorAll('.remove-member-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const email = btn.dataset.email;
      if (await confirm('Üyeyi Kaldır', `${email} adresli üyeyi ekipten çıkarmak istediğinize emin misiniz?`)) {
        try {
          await removeUserFromSite(site.id, email);
          toast.success('Üye kaldırıldı');
          // Refresh to show changes immediately easier than DOM manipulation here
          hideModal('team-modal');
          router.handleRoute(); // Rough way to refresh data
        } catch (e) {
          console.error(e);
          toast.error('Üye kaldırılamadı');
        }
      }
    });
  });
}

async function handleInviteUser(site) {
  const emailInput = document.getElementById('invite-email');
  const email = emailInput.value.trim();

  if (!email) {
    toast.error('E-posta adresi gereklidir');
    return;
  }

  // Very basic email validation
  if (!email.includes('@')) {
    toast.error('Geçerli bir e-posta adresi giriniz');
    return;
  }

  const permissions = {
    canCreateLog: document.getElementById('perm-create-log').checked,
    canDeleteLog: document.getElementById('perm-delete-log').checked,
    canUploadAsset: document.getElementById('perm-upload-asset').checked,
    canDeleteAsset: document.getElementById('perm-delete-asset').checked,
    role: 'member'
  };

  const btn = document.getElementById('send-invite-btn');
  const originalText = btn.textContent;
  btn.textContent = 'Ekleniyor...';
  btn.disabled = true;

  try {
    await inviteUserToSite(site.id, email, permissions);
    toast.success(`Davet edildi: ${email}`);
    emailInput.value = '';
    hideModal('team-modal');
    router.handleRoute(); // Refresh
  } catch (error) {
    console.error('Invite error:', error);
    toast.error('Davet başarısız oldu');
    btn.textContent = originalText;
    btn.disabled = false;
  }
}

/**
 * Handle update site
 * @param {string} siteId 
 */
async function handleUpdateSite(siteId) {
  const name = document.getElementById('edit-site-name').value.trim();

  if (!name) {
    toast.error('Alan adı gereklidir');
    return;
  }

  const siteData = {
    name,
    location: document.getElementById('edit-site-location').value.trim(),
    coordinates: document.getElementById('edit-site-coords').value.trim(),
    period: document.getElementById('edit-site-period').value,
    supervisor: document.getElementById('edit-site-supervisor').value.trim(),
    excavationCode: document.getElementById('edit-site-code').value.trim(),
    description: document.getElementById('edit-site-description').value.trim()
  };

  try {
    await updateSite(siteId, siteData);
    toast.success('Alan güncellendi!');
    hideModal('edit-site-modal');
    router.handleRoute();
  } catch (error) {
    console.error('Update site error:', error);
    toast.error('Alan güncellenemedi. Lütfen tekrar deneyin.');
  }
}

/**
 * Escape HTML for safe rendering
 * @param {string} str 
 * @returns {string}
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
